// Main App Component with Comprehensive Routing

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ProtectedRoute from '@components/ProtectedRoute';
import AdminLayout from '@layouts/AdminLayout';
import ClientLayout from '@layouts/ClientLayout';

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Auth Pages (loaded eagerly - small and needed immediately)
import LoginPage from '@pages/auth/LoginPage';
import RegisterPage from '@pages/auth/RegisterPage';
import AdminLoginPage from '@pages/auth/AdminLoginPage';

// Public Pages (loaded eagerly - landing page)
import LandingPage from '@pages/public/LandingPage';

// Client Pages (lazy loaded)
const ClientDashboard = lazy(() => import('@pages/client/DashboardPage'));
const CartPage = lazy(() => import('@pages/client/cart/CartPage'));
const CheckoutPage = lazy(() => import('@pages/client/checkout/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('@pages/client/orders/OrderSuccessPage'));
const ClientOrdersListPage = lazy(() => import('@pages/client/orders/OrdersListPage'));
const ClientOrderDetailPage = lazy(() => import('@pages/client/orders/OrderDetailPage'));
const ClientAnnouncementsPage = lazy(() => import('@pages/client/Announcements'));
const ClientDownloadsPage = lazy(() => import('@pages/client/Downloads'));
const ClientServicesPage = lazy(() => import('@pages/client/ServicesPage'));
const ClientInvoicesPage = lazy(() => import('@pages/client/InvoicesPage'));
const ClientTicketsPage = lazy(() => import('@pages/client/TicketsPage'));
const ClientDomainsPage = lazy(() => import('@pages/client/DomainsPage'));
const StorefrontPage = lazy(() => import('@pages/client/StorefrontPage'));
const ServiceDetailPage = lazy(() => import('@pages/client/ServiceDetailPage'));
const InvoiceDetailPage = lazy(() => import('@pages/client/InvoiceDetailPage'));
const TicketDetailPage = lazy(() => import('@pages/client/TicketDetailPage'));
const NewTicketPage = lazy(() => import('@pages/client/NewTicketPage'));
const AccountPage = lazy(() => import('@pages/client/AccountPage'));
const InvoicePayment = lazy(() => import('@/areas/client/Invoices/InvoicePayment').then(m => ({ default: m.InvoicePayment })));

// Admin Pages (lazy loaded)
const AdminDashboardPage = lazy(() => import('@pages/admin/DashboardPage'));
const AdminOrdersListPage = lazy(() => import('@pages/admin/orders/OrdersListPage'));
const AdminOrderDetailPage = lazy(() => import('@pages/admin/orders/OrderDetailPage'));
const ClientsListPage = lazy(() => import('@pages/admin/clients/ClientsListPage'));
const ClientDetailPage = lazy(() => import('@pages/admin/clients/ClientDetailPage'));
const ProductsListPage = lazy(() => import('@pages/admin/products/ProductsListPage'));
const WebhooksPage = lazy(() => import('@pages/admin/WebhooksPage'));
const PromotionsPage = lazy(() => import('@pages/admin/Promotions'));
const AnalyticsDashboardPage = lazy(() => import('@pages/admin/AnalyticsDashboard'));
const FinancialReportsPage = lazy(() => import('@pages/admin/FinancialReports'));
const CustomerAnalyticsPage = lazy(() => import('@pages/admin/CustomerAnalytics'));
const AdminKnowledgeBasePage = lazy(() => import('@pages/admin/KnowledgeBase'));
const AdminAnnouncementsPage = lazy(() => import('@pages/admin/Announcements'));
const AdminDownloadsPage = lazy(() => import('@pages/admin/Downloads'));
const AdminNetworkStatusPage = lazy(() => import('@pages/admin/NetworkStatus'));
const LanguagesPage = lazy(() => import('@pages/admin/Languages'));
const CurrenciesPage = lazy(() => import('@pages/admin/Currencies'));
const TaxSettingsPage = lazy(() => import('@pages/admin/TaxSettings'));
const AdminInvoicesPage = lazy(() => import('@pages/admin/InvoicesPage'));
const AdminInvoiceDetailPage = lazy(() => import('@pages/admin/InvoiceDetailPage'));
const AdminServicesPage = lazy(() => import('@pages/admin/ServicesPage'));
const AdminServiceDetailPage = lazy(() => import('@pages/admin/ServiceDetailPage'));
const AdminTicketsPage = lazy(() => import('@pages/admin/TicketsPage'));
const AdminTicketDetailPage = lazy(() => import('@pages/admin/TicketDetailPage'));
const AdminTicketDepartmentsPage = lazy(() => import('@pages/admin/TicketDepartmentsPage'));
const AdminCannedResponsesPage = lazy(() => import('@pages/admin/CannedResponsesPage'));
const AdminDomainsPage = lazy(() => import('@pages/admin/DomainsPage'));
const AdminEmailTemplatesPage = lazy(() => import('@pages/admin/EmailTemplatesPage'));
const ClientGroupsPage = lazy(() => import('@pages/admin/ClientGroupsPage'));
const ProductGroupsPage = lazy(() => import('@pages/admin/ProductGroupsPage'));
const AddonsPage = lazy(() => import('@pages/admin/AddonsPage'));
const TLDPricingPage = lazy(() => import('@pages/admin/TLDPricingPage'));
const RegistrarsPage = lazy(() => import('@pages/admin/RegistrarsPage'));
const IntegrationsPage = lazy(() => import('@pages/admin/IntegrationsPage'));
const AdsManagerPage = lazy(() => import('@pages/admin/AdsManagerPage'));
const ProtectCordPage = lazy(() => import('@pages/admin/ProtectCordPage'));
const GeneralSettingsPage = lazy(() => import('@pages/admin/GeneralSettingsPage'));
const PaymentGatewaysPage = lazy(() => import('@pages/admin/PaymentGatewaysPage'));
const AffiliatesPage = lazy(() => import('@pages/admin/AffiliatesPage'));
const ServersPage = lazy(() => import('@pages/admin/ServersPage'));

// Affiliate Pages (lazy loaded)
const AffiliateDashboard = lazy(() => import('@pages/affiliate/Dashboard'));
const AffiliatePayouts = lazy(() => import('@pages/affiliate/Payouts'));
const AffiliateReports = lazy(() => import('@pages/affiliate/Reports'));
const AffiliateReportsAdmin = lazy(() => import('@pages/admin/AffiliateReports'));

// Public Pages (lazy loaded - less critical)
const PublicKnowledgeBasePage = lazy(() => import('@pages/public/KnowledgeBase'));
const PublicNetworkStatusPage = lazy(() => import('@pages/public/NetworkStatus'));

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Public Knowledge Base */}
        <Route path="/knowledge-base" element={<PublicKnowledgeBasePage />} />
        <Route path="/knowledge-base/:slug" element={<PublicKnowledgeBasePage />} />

        {/* Public Network Status */}
        <Route path="/network-status" element={<PublicNetworkStatusPage />} />

        {/* Client Routes (Protected) */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientDashboard />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/cart"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <CartPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/checkout"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <CheckoutPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/orders/:orderId/success"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <OrderSuccessPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/orders/:orderId"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientOrderDetailPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/orders"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientOrdersListPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/announcements"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientAnnouncementsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/downloads"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientDownloadsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/services/:id"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ServiceDetailPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/services"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientServicesPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/invoices/:id/pay"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <InvoicePayment />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/invoices/:id"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <InvoiceDetailPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/invoices"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientInvoicesPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/tickets/new"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <NewTicketPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/tickets/:id"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <TicketDetailPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/tickets"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientTicketsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/domains"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientDomainsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/store"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <StorefrontPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/account"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <AccountPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes (Protected - Require Admin) */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Clients */}
        <Route
          path="/admin/clients/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ClientDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ClientsListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Products */}
        <Route
          path="/admin/products"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ProductsListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Orders */}
        <Route
          path="/admin/orders/:orderId"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminOrderDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminOrdersListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Invoices */}
        <Route
          path="/admin/invoices/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminInvoiceDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminInvoicesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Services */}
        <Route
          path="/admin/services/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminServiceDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/services"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminServicesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Domains */}
        <Route
          path="/admin/domains/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDomainsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/domains"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDomainsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Tickets */}
        <Route
          path="/admin/tickets/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminTicketDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminTicketsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ticket-departments"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminTicketDepartmentsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/canned-responses"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminCannedResponsesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Marketing */}
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminAnnouncementsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/promotions"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <PromotionsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/affiliates"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AffiliatesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/affiliate-reports"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AffiliateReportsAdmin />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Servers */}
        <Route
          path="/admin/servers"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ServersPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Content */}
        <Route
          path="/admin/knowledge-base"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminKnowledgeBasePage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/downloads"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDownloadsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Analytics */}
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AnalyticsDashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/financial-reports"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <FinancialReportsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/customer-analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <CustomerAnalyticsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* System */}
        <Route
          path="/admin/network-status"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminNetworkStatusPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/webhooks"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <WebhooksPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminEmailTemplatesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Client Groups */}
        <Route
          path="/admin/client-groups"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ClientGroupsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Product Groups */}
        <Route
          path="/admin/product-groups"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ProductGroupsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Addons */}
        <Route
          path="/admin/addons"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AddonsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* TLD Pricing */}
        <Route
          path="/admin/tld-pricing"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <TLDPricingPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Registrars */}
        <Route
          path="/admin/registrars"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <RegistrarsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Emails redirect to Email Templates */}
        <Route
          path="/admin/emails"
          element={<Navigate to="/admin/email-templates" replace />}
        />

        {/* Integrations */}
        <Route
          path="/admin/integrations"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <IntegrationsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Ads Manager */}
        <Route
          path="/admin/ads"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdsManagerPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* ProtectCord */}
        <Route
          path="/admin/protectcord"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ProtectCordPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="/admin/settings/general"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <GeneralSettingsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/payment-gateways"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <PaymentGatewaysPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/languages"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <LanguagesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/currencies"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <CurrenciesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/tax"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <TaxSettingsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Affiliate Portal */}
        <Route
          path="/affiliate/dashboard"
          element={
            <ProtectedRoute>
              <AffiliateDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/payouts"
          element={
            <ProtectedRoute>
              <AffiliatePayouts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/reports"
          element={
            <ProtectedRoute>
              <AffiliateReports />
            </ProtectedRoute>
          }
        />

        {/* 404 Not Found */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
              <div className="text-center animate-fade-in">
                <h1 className="text-8xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-display">404</h1>
                <p className="mt-4 text-xl text-slate-400">Page not found</p>
                <p className="mt-2 text-sm text-slate-500">The page you're looking for doesn't exist or has been moved.</p>
                <a href="/" className="mt-6 inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 font-medium">
                  Go Home
                </a>
              </div>
            </div>
          }
        />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
